import CoinbaseWalletSDK from "@coinbase/wallet-sdk";
import { constants } from "config";
import {
  getDisconnectedCookie,
  setDisconnectedCookie,
  deleteDisconnectedCookie,
} from "utils/storage";
import { Connector, ConnectorDispatch, CoinBaseProvider } from "../types";

/**
 * Convert chainId returned from CoinBase Wallet events/requests
 */
const parseChainId = (chainId: string | number) =>
  typeof chainId === "number"
    ? chainId
    : Number.parseInt(chainId, chainId.startsWith("0x") ? 16 : 10);

class CoinBaseConnector extends Connector {
  public provider: CoinBaseProvider | undefined;

  /**
   * Store any in-progress immediate connection to use if manual connection is attempted
   */
  private immediateConnection?: Promise<void>;

  /**
   * A `CoinbaseWalletSDK` instance.
   */
  public coinBaseWallet: CoinbaseWalletSDK | undefined;

  constructor(dispatch: ConnectorDispatch) {
    super(dispatch);

    // Pretend we don't have a connection if user attempted to disconnect
    if (!getDisconnectedCookie(this.constructor.name)) {
      this.connectImmediately();
    }
  }

  /**
   * Set up event handlers on provider object
   */
  private async initialize(): Promise<void> {
    // If called by connect() while connectImmediately() is still running, don't overwrite original promise
    if (this.immediateConnection) return this.immediateConnection;

    this.coinBaseWallet = new CoinbaseWalletSDK({ appName: "HUG.xyz" });

    // Store connection promise in case of above edge case
    await (this.immediateConnection = Promise.resolve(
      this.coinBaseWallet.makeWeb3Provider(constants.alchemyUrl)
    ).then((provider) => {
      this.provider = provider as CoinBaseProvider;

      // Update AuthContext state on any connection change events
      this.provider.on("connect", ({ chainId }): void => {
        this.dispatch({ type: "update", chainId: parseChainId(chainId) });
      });

      this.provider.on("disconnect", (error): void => {
        this.dispatch({ type: "error", error });
      });

      this.provider.on("chainChanged", (chainId: string): void => {
        this.dispatch({ type: "update", chainId: parseChainId(chainId) });
      });
    }));

    return undefined;
  }

  public async connectImmediately(): Promise<void> {
    await this.initialize();

    if (!this.provider) return this.dispatch({ type: "reset" });

    return Promise.all([
      this.provider.request({ method: "eth_chainId" }) as Promise<string>,
      // NOTE: use eth_accounts instead of eth_requestAccounts assuming we have permisions, if we don't
      // then we can't reconnect the user since they haven't connected yet
      this.provider.request({ method: "eth_accounts" }) as Promise<string[]>,
    ])
      .then(([chainId, accounts]) => {
        if (accounts.length) {
          this.dispatch({
            type: "update",
            chainId: parseChainId(chainId),
            accounts,
          });
        } else {
          throw new Error("No accounts from CoinBase Wallet provider");
        }
      })
      .catch(() => {
        // Fail silently, most commonly because user hasn't connected yet
        // console.debug("Couldn't connect immediately", error);
        this.dispatch({ type: "reset" });
      });
  }

  public async connect(): Promise<void> {
    this.dispatch({ type: "startConnecting" });

    await this.initialize();

    if (!this.provider) {
      // Initialize should link to wallet app or show install modal, so something went wrong at this point
      return this.dispatch({
        type: "error",
        error: new Error("No Coinbase Wallet"),
      });
    }

    return Promise.all([
      this.provider.request({ method: "eth_chainId" }) as Promise<string>,
      this.provider?.request({ method: "eth_requestAccounts" }) as Promise<
        string[]
      >,
      // NOTE: the extra request for wallet permissions is to allow users to "reconnect" if they've
      // previously disconnected. It should open the extension and allow them to select a different
      // wallet address, although the original would still be connected in the extension. See
      // this.disconnect() for more info.
      // this.provider.request({ method: "wallet_requestPermissions", params: [{ eth_accounts: {} }] })
      //   .then(() => this.provider?.request({ method: "eth_requestAccounts" })) as Promise<string[]>,
    ])
      .then(([chainId, accounts]) => {
        // TODO: can verify appropriate chainId here before updating

        // Once manual connect is successful, ensure previous disconnect attempt is removed to allow reconnect
        deleteDisconnectedCookie(this.constructor.name);

        return this.dispatch({
          type: "update",
          chainId: parseChainId(chainId),
          accounts,
        });
      })
      .catch((error) => {
        this.dispatch({ type: "error", error });
      });
  }

  public disconnect(): void {
    setDisconnectedCookie(this.constructor.name);
    this.coinBaseWallet?.disconnect();
  }
}

export { CoinBaseConnector };
